import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import WKT from 'ol/format/WKT';
import { MultiPolygon, Polygon } from 'ol/geom';
import { transform } from 'ol/proj';
import type { Position } from 'geojson';
import { Blast, Hole, HoleGeologyRow, HoleLoad, HoleService, HoleStatus } from 'src/app/services/hole.service';
import { FonctionsService } from 'src/app/services/fonctions-partagees.service';
import { SiteHoleTolerance, toSiteHoleTolerance } from 'src/app/bo/site-hole-tolerance';
import { HttpService } from 'src/app/services/http.service';
import { DEFAULT_SHIFT_CONFIG, DrillActivityService } from 'src/app/services/drill-activity.service';
import { ShiftConfigService } from 'src/app/services/shift-config.service';
import { DrillSessionApiDto, ShiftConfig, shiftConfigFromDto } from 'src/app/shared/models/drilling.models';
import { DeliveriesService } from 'src/app/services/deliveries.service';
import { Delivery, JsonDelivery } from 'src/app/bo/delivery';
import { UtilisateurService } from 'src/app/services/utilisateur.service';
import { BlastPolygonWKTDto, PolygonType } from 'src/app/bo/polygon/tirsPolygone';
import { GeologyModel } from 'src/app/components/blast3-dviewer/geology-model';
import { HolesMapMode } from './blast-holes.types';
import { withCalculatedValues } from './shared/hole-calculations';
import { HoleCellPolygon, computeHoleCells } from './shared/hole-cells';
import { ensureProjection, normalizeEpsgCode } from './shared/projection.utils';

/** Blast polygon types usable as the blast contour: drilling contour first, else automatic-from-holes. */
const CONTOUR_POLYGON_TYPES = [PolygonType.AutomaticFromHoles, PolygonType.DrillingContour];

/** Hole status shown on the map: one status, or every status ("actual"). */
export type HoleStatusFilter = HoleStatus | 'actual';

/** Per-hole cell colours drawn under the holes by the map (publicGuid → CSS colour, null = outline only). */
export interface HoleCellOverlay {
  fills: ReadonlyMap<string, string | null>;
}

type FetchPolicy = 'cache-first' | 'network-only';

/** Best-effort human-readable message from an Apollo / HTTP / plain error. */
function describeError(error: unknown): string {
  if (!error) return '';
  const anyError = error as { message?: string; graphQLErrors?: { message: string }[]; networkError?: { message?: string; status?: number } };
  const graphQl = anyError.graphQLErrors?.map(e => e.message).filter(Boolean) ?? [];
  if (graphQl.length) return graphQl.join(' | ');
  if (anyError.networkError) return `${anyError.networkError.status ?? ''} ${anyError.networkError.message ?? ''}`.trim();
  return anyError.message ?? String(error);
}

/**
 * State shared by the Holes tab container, its map and its views.
 * Provided by BlastHolesComponent (one instance per Holes tab, not root).
 */
@Injectable()
export class BlastHolesStateService {
  private readonly holeService      = inject(HoleService);
  private readonly fonctionsService = inject(FonctionsService);
  private readonly http             = inject(HttpService);
  private readonly drillActivity    = inject(DrillActivityService);
  private readonly shiftConfigService = inject(ShiftConfigService);
  private readonly deliveriesService  = inject(DeliveriesService);
  private readonly userService        = inject(UtilisateurService);

  private loadSeq = 0;

  readonly blastId        = signal<number | null>(null);
  readonly blast          = signal<Blast | null>(null);
  readonly isLoading      = signal(false);
  readonly loadError      = signal(false);
  readonly selectedHoleId = signal<string | null>(null);
  readonly searchTerm     = signal('');
  readonly mapMode        = signal<HolesMapMode>('top');
  readonly statusFilter   = signal<HoleStatusFilter>('actual');
  /** Set by a view (e.g. Geology) to colour a Voronoi cell per hole; null hides the cells. */
  readonly cellOverlay    = signal<HoleCellOverlay | null>(null);
  /** Set by the Geology view: the block model the 3D viewer builds under the holes; null removes it. */
  readonly geologyModel   = signal<GeologyModel | null>(null);
  /** Set by a view (e.g. Quality) to colour the hole markers (publicGuid → CSS colour); null restores status icons. */
  readonly holeColors     = signal<ReadonlyMap<string, string | null> | null>(null);

  // Loading data (loaded on demand by the Quality view), keyed by hole id and by hole name
  readonly loading        = signal<ReadonlyMap<string, HoleLoad> | null>(null);
  readonly loadingLoading = signal(false);
  readonly loadingError   = signal(false);
  readonly loadingErrorMessage = signal<string | null>(null);
  private loadingSeq = 0;

  // Site hole tolerances (loaded on demand by the Quality view)
  readonly tolerance        = signal<SiteHoleTolerance | null>(null);
  readonly toleranceLoading = signal(false);
  readonly toleranceError   = signal(false);
  private toleranceSiteId: number | null = null;

  // Drilling sessions (loaded on demand by the Drilling session view)
  readonly drillSessions        = signal<DrillSessionApiDto[] | null>(null);
  readonly drillSessionsLoading = signal(false);
  readonly drillSessionsError   = signal(false);
  readonly drillSessionSaving   = signal(false);
  readonly drillSessionSaveError = signal(false);
  /** Selected drilling session (its holes are highlighted on the map). */
  readonly selectedSessionId    = signal<string | null>(null);
  private drillSeq = 0;

  // Deliveries (loaded on demand by the Delivery view)
  readonly deliveries        = signal<Delivery[] | null>(null);
  readonly deliveriesLoading = signal(false);
  readonly deliveriesError   = signal(false);
  /** Selected delivery (its holes are highlighted on the map). */
  readonly selectedDeliveryId = signal<string | null>(null);
  /** Delivery log (holes, quantities) per delivery guid, fetched when a delivery is selected or opened. */
  readonly deliveryLogs = signal<ReadonlyMap<string, JsonDelivery>>(new Map());
  private readonly deliveryLogRequests = new Map<string, Promise<JsonDelivery | null>>();
  private deliveriesSeq = 0;

  // Site shift configuration (labels / start hours), defaults until loaded
  readonly shiftConfig = signal<ShiftConfig>(DEFAULT_SHIFT_CONFIG);
  private shiftConfigSiteId: number | null = null;

  // Geology (loaded on demand by the Geology view)
  readonly geology        = signal<ReadonlyMap<string, HoleGeologyRow> | null>(null);
  readonly geologyLoading = signal(false);
  readonly geologyError   = signal(false);
  /** Technical detail of the last geology failure (GraphQL / network message), for the notice. */
  readonly geologyErrorMessage = signal<string | null>(null);
  private geologySeq = 0;

  readonly holes = computed<Hole[]>(() => this.blast()?.BlastDesign ?? []);

  // Blast contour (exterior rings in the site projection), loaded with the blast
  readonly contour        = signal<readonly Position[][] | null>(null);
  readonly contourLoading = signal(false);
  private contourSeq = 0;

  /**
   * Voronoi cell of every positioned hole, in the site projection, cut to the blast contour when
   * one is known. Shared by the map (Geology cells) and the Quality view (powder factor volume).
   */
  readonly holeCells = computed<ReadonlyMap<string, HoleCellPolygon>>(() => {
    const sites = this.holes()
      .filter(hole => hole.PositionPlannedXYZ)
      .map(hole => ({ id: hole.publicGuid, x: hole.PositionPlannedXYZ.x, y: hole.PositionPlannedXYZ.y }));
    return computeHoleCells(sites, this.contour());
  });

  readonly selectedHole = computed<Hole | null>(() => {
    const id = this.selectedHoleId();
    return id ? this.holes().find(h => h.publicGuid === id) ?? null : null;
  });

  async load(blastId: number, policy: FetchPolicy = 'cache-first'): Promise<void> {
    const seq = ++this.loadSeq;
    this.blastId.set(blastId);
    this.isLoading.set(true);
    this.loadError.set(false);
    // Drop the previous blast right away so the map and the views never show stale holes.
    this.blast.set(null);
    this.selectedHoleId.set(null);
    this.geology.set(null);
    this.geologyError.set(false);
    this.loading.set(null);
    this.loadingError.set(false);
    this.drillSessions.set(null);
    this.drillSessionsError.set(false);
    this.selectedSessionId.set(null);
    this.deliveries.set(null);
    this.deliveriesError.set(false);
    this.selectedDeliveryId.set(null);
    this.deliveryLogs.set(new Map());
    this.deliveryLogRequests.clear();
    this.contour.set(null);
    this.fonctionsService.loading_screen(true, 20000);
    void this.loadContour(blastId);

    try {
      const blast = await this.holeService.GetBlastDesign(blastId, policy);
      if (seq !== this.loadSeq) return;
      this.blast.set(withCalculatedValues(blast));
      this.selectedHoleId.set(null);
    } catch (error) {
      if (seq !== this.loadSeq) return;
      console.error('BlastHolesState: failed to load blast design', error);
      this.blast.set(null);
      this.loadError.set(true);
    } finally {
      if (seq === this.loadSeq) {
        this.isLoading.set(false);
        this.fonctionsService.loading_screen(false, 20000);
      }
    }
  }

  /** Re-fetch from the server (after a hole import / edit). */
  reload(): Promise<void> {
    const id = this.blastId();
    return id ? this.load(id, 'network-only') : Promise.resolve();
  }

  /**
   * Blast contour from `TirsPolygone/{siteId}/tirsPolygoneByIdTir`: the drilling contour when the
   * blast has one, else the automatic one. Stored as rings in the site projection (metres); null
   * without polygon, in which case the hole cells fall back to a box around the holes.
   */
  private async loadContour(blastId: number): Promise<void> {
    const site = this.userService.currentSite();
    if (!site?.siteId || !site.epsg) return;

    const seq = ++this.contourSeq;
    this.contourLoading.set(true);
    try {
      const polygons = await firstValueFrom(this.http.post<unknown, BlastPolygonWKTDto[]>(
        `TirsPolygone/${site.siteId}/tirsPolygoneByIdTir`,
        { idsTirs: [blastId], idsTirsPolygoneType: CONTOUR_POLYGON_TYPES },
      ));
      if (seq !== this.contourSeq) return;

      // `blastId` is a BlastId value object on the API side ({ value }), `type` may come as a name or a number.
      const idOf   = (polygon: BlastPolygonWKTDto) => Number((polygon.blastId as unknown as { value?: unknown })?.value ?? polygon.blastId);
      const typeOf = (polygon: BlastPolygonWKTDto) => typeof polygon.type === 'string'
        ? PolygonType[polygon.type as keyof typeof PolygonType]
        : Number(polygon.type);
      const ofBlast = (polygons ?? []).filter(polygon => idOf(polygon) === blastId && polygon.wktPolygon);
      const chosen = ofBlast.find(polygon => typeOf(polygon) === PolygonType.DrillingContour)
        ?? ofBlast.find(polygon => typeOf(polygon) === PolygonType.AutomaticFromHoles)
        ?? ofBlast[0];
      if (!chosen) {
        console.warn('BlastHolesState: no contour found for blast', blastId, '— hole cells fall back to a box');
        return;
      }

      const geometry = new WKT().readGeometry(chosen.wktPolygon);
      const polygonsOf = geometry instanceof MultiPolygon ? geometry.getPolygons()
                       : geometry instanceof Polygon      ? [geometry]
                       : [];
      let rings: Position[][] = polygonsOf
        .map(polygon => polygon.getLinearRing(0)?.getCoordinates() ?? [])
        .filter(ring => ring.length >= 3);

      const contourEpsg = normalizeEpsgCode(chosen.epsg || site.epsg);
      const siteEpsg = normalizeEpsgCode(site.epsg);
      if (contourEpsg !== siteEpsg) {
        const [contourProjection, siteProjection] = await Promise.all([
          ensureProjection(this.http, chosen.epsg || site.epsg),
          ensureProjection(this.http, site.epsg),
        ]);
        if (seq !== this.contourSeq) return;
        rings = rings.map(ring => ring.map(coordinate => transform(coordinate, contourProjection, siteProjection)));
      }
      this.contour.set(rings);
    } catch (error) {
      if (seq !== this.contourSeq) return;
      console.error('BlastHolesState: unable to load the blast contour', error);
    } finally {
      if (seq === this.contourSeq) this.contourLoading.set(false);
    }
  }

  /** Loads drill logs and lithology samples once per blast (Geology view). */
  async loadGeology(force = false): Promise<void> {
    const blastId = this.blastId();
    if (!blastId || (this.geology() && !force) || this.geologyLoading()) return;

    const seq = ++this.geologySeq;
    this.geologyLoading.set(true);
    this.geologyError.set(false);
    this.geologyErrorMessage.set(null);
    try {
      const rows = await this.holeService.GetBlastGeology(blastId, force ? 'network-only' : 'cache-first');
      if (seq !== this.geologySeq) return;
      this.geology.set(new Map(rows.map(row => [row.publicGuid, row])));
    } catch (error) {
      if (seq !== this.geologySeq) return;
      console.error('BlastHolesState: failed to load blast geology', error);
      this.geologyError.set(true);
      this.geologyErrorMessage.set(describeError(error));
    } finally {
      if (seq === this.geologySeq) this.geologyLoading.set(false);
    }
  }

  /** Loads the per-hole loading quantities once per blast (Quality view: bulk / stemming). */
  async loadLoading(force = false): Promise<void> {
    const blastId = this.blastId();
    if (!blastId || (this.loading() && !force) || this.loadingLoading()) return;

    const seq = ++this.loadingSeq;
    this.loadingLoading.set(true);
    this.loadingError.set(false);
    this.loadingErrorMessage.set(null);
    try {
      const blast = await this.holeService.GetBlastLoading(blastId, force ? 'network-only' : 'cache-first');
      if (seq !== this.loadingSeq) return;
      const byKey = new Map<string, HoleLoad>();
      for (const load of blast?.BlastLoading ?? []) {
        if (load.holeId) byKey.set(load.holeId, load);
        if (load.name)   byKey.set(load.name, load);
      }
      this.loading.set(byKey);
    } catch (error) {
      if (seq !== this.loadingSeq) return;
      console.error('BlastHolesState: failed to load blast loading', error);
      this.loadingError.set(true);
      this.loadingErrorMessage.set(describeError(error));
    } finally {
      if (seq === this.loadingSeq) this.loadingLoading.set(false);
    }
  }

  /** Loading quantities of a hole, matched by id first and by name as a fallback. */
  loadFor(hole: Hole): HoleLoad | undefined {
    const loading = this.loading();
    return loading?.get(hole.publicGuid) ?? loading?.get(hole.name);
  }

  /** Loads the site hole tolerances once per site (Quality view thresholds). */
  async loadTolerance(siteId: number, force = false): Promise<void> {
    if (!siteId || this.toleranceLoading()) return;
    if (this.toleranceSiteId === siteId && this.tolerance() && !force) return;

    this.toleranceSiteId = siteId;
    this.toleranceLoading.set(true);
    this.toleranceError.set(false);
    try {
      const tolerance = await firstValueFrom(this.holeService.getSiteHoleTolerance(siteId));
      if (this.toleranceSiteId !== siteId) return;
      this.tolerance.set(tolerance ? toSiteHoleTolerance(tolerance) : null);
    } catch (error) {
      if (this.toleranceSiteId !== siteId) return;
      console.error('BlastHolesState: failed to load site hole tolerance', error);
      this.tolerance.set(null);
      this.toleranceError.set(true);
    } finally {
      if (this.toleranceSiteId === siteId) this.toleranceLoading.set(false);
    }
  }

  /** Loads the drilling sessions of the blast once (Drilling session view). */
  async loadDrillSessions(force = false): Promise<void> {
    const blastId = this.blastId();
    if (!blastId || (this.drillSessions() && !force) || this.drillSessionsLoading()) return;

    const seq = ++this.drillSeq;
    this.drillSessionsLoading.set(true);
    this.drillSessionsError.set(false);
    try {
      const sessions = await firstValueFrom(this.drillActivity.fetchSessionsByBlastId(blastId));
      if (seq !== this.drillSeq) return;
      this.drillSessions.set(sessions ?? []);
    } catch (error) {
      if (seq !== this.drillSeq) return;
      console.error('BlastHolesState: failed to load drilling sessions', error);
      this.drillSessionsError.set(true);
    } finally {
      if (seq === this.drillSeq) this.drillSessionsLoading.set(false);
    }
  }

  /** Creates (no id) or updates a drilling session, then re-fetches the list. Resolves to true on success. */
  async saveDrillSession(dto: DrillSessionApiDto): Promise<boolean> {
    if (this.drillSessionSaving()) return false;
    this.drillSessionSaving.set(true);
    this.drillSessionSaveError.set(false);
    try {
      const request = dto.id
        ? this.http.put<DrillSessionApiDto, DrillSessionApiDto>('DrillSessions', dto)
        : this.http.post<DrillSessionApiDto, DrillSessionApiDto>('DrillSessions', dto);
      await firstValueFrom(request);
      await this.loadDrillSessions(true);
      return true;
    } catch (error) {
      console.error('BlastHolesState: failed to save drilling session', error);
      this.drillSessionSaveError.set(true);
      return false;
    } finally {
      this.drillSessionSaving.set(false);
    }
  }

  /** Loads the site shift configuration once per site; keeps the defaults on failure. */
  async loadShiftConfig(siteId: number): Promise<void> {
    if (!siteId || this.shiftConfigSiteId === siteId) return;
    this.shiftConfigSiteId = siteId;
    try {
      const dto = await firstValueFrom(this.shiftConfigService.getShiftConfig(siteId));
      if (this.shiftConfigSiteId !== siteId) return;
      if (dto?.shiftsPerDay > 0 && dto.shiftLabels?.length) this.shiftConfig.set(shiftConfigFromDto(dto));
    } catch (error) {
      if (this.shiftConfigSiteId !== siteId) return;
      console.warn('BlastHolesState: no site shift config, using defaults', error);
      this.shiftConfig.set(DEFAULT_SHIFT_CONFIG);
    }
  }

  toggleSession(sessionId: string): void {
    this.selectedSessionId.update(current => (current === sessionId ? null : sessionId));
  }

  /** Loads the deliveries of the blast once (Delivery view); `force` re-fetches after an edit. */
  async loadDeliveries(force = false): Promise<void> {
    const blastId = this.blastId();
    if (!blastId || (this.deliveries() && !force) || this.deliveriesLoading()) return;

    const seq = ++this.deliveriesSeq;
    this.deliveriesLoading.set(true);
    this.deliveriesError.set(false);
    try {
      const deliveries = await firstValueFrom(this.deliveriesService.getDeliveriesByTirId(blastId)) as Delivery[] | null;
      if (seq !== this.deliveriesSeq) return;
      this.deliveries.set(deliveries ?? []);
      if (force) {
        // Logs may have changed (holes moved / split): drop the cache, keep the selection
        this.deliveryLogs.set(new Map());
        this.deliveryLogRequests.clear();
      }
    } catch (error) {
      if (seq !== this.deliveriesSeq) return;
      console.error('BlastHolesState: failed to load deliveries', error);
      this.deliveriesError.set(true);
    } finally {
      if (seq === this.deliveriesSeq) this.deliveriesLoading.set(false);
    }
  }

  /**
   * Fetches the delivery log (holes and quantities) of one delivery, once per guid.
   * Resolves to null when the request fails (a notice is left to the caller).
   */
  loadDeliveryLog(deliveryId: string): Promise<JsonDelivery | null> {
    const cached = this.deliveryLogs().get(deliveryId);
    if (cached) return Promise.resolve(cached);
    const pending = this.deliveryLogRequests.get(deliveryId);
    if (pending) return pending;

    const blastId = this.blastId();
    if (!blastId) return Promise.resolve(null);

    const request = firstValueFrom(this.deliveriesService.getDeliveryByGuid(blastId, deliveryId))
      .then(log => {
        if (log && this.blastId() === blastId) {
          this.deliveryLogs.update(logs => new Map(logs).set(deliveryId, log));
        }
        return log ?? null;
      })
      .catch(error => {
        console.error('BlastHolesState: failed to load delivery log', deliveryId, error);
        return null;
      })
      .finally(() => this.deliveryLogRequests.delete(deliveryId));
    this.deliveryLogRequests.set(deliveryId, request);
    return request;
  }

  toggleDelivery(deliveryId: string): void {
    this.selectedDeliveryId.update(current => (current === deliveryId ? null : deliveryId));
  }

  selectHole(publicGuid: string | null): void {
    this.selectedHoleId.set(publicGuid);
  }

  toggleHole(publicGuid: string): void {
    this.selectedHoleId.update(current => (current === publicGuid ? null : publicGuid));
  }
}
